import React from 'react';

import { QuestionnaireHomeProps } from "./questionnaire-home.types";
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';
import { FieldPickerOnChangeValue } from '../field-picker';
import { QuestionnaireType } from '@common/dto/questionnaire/questionnaire-type';
import { generateId } from '@common/utils/common';
import { SkipLogicCondition, SkipLogicConditionOperator } from '@common/components/skip-logic/skip-logic.types';
import { QuestionnaireSkipLogicConditionCreateDto } from '@common/dto/questionnaire/skip-logic/condition-create-dto';
import { QuestionnaireSkipLogicConditionListDto } from '@common/dto/questionnaire/skip-logic/condition-list-dto';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { QuestionnairePageType } from '@common/dto/questionnaire/page/type';
import isNil from 'lodash/isNil';
import { FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT } from '@modules/fhq-design-tool/constants/shared';
import { QuestionnaireFieldGetDto } from '@common/dto/questionnaire/field/get-dto';
import { QuestionnairePageUpdateDto } from '@common/dto/questionnaire/page/update-dto';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';
import { QuestionnairePageCreateLocalDto } from '@modules/fhq-design-tool/dto/page-create-local-dto';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { QuestionnaireDesignerTabUpdateLocalDto } from '@modules/fhq-design-tool/dto/designer-update-local-dto';
import { QuestionnaireLanguage } from '@common/dto/questionnaire/language';
import { FormatPickerOnChangeValue } from '../format-picker/format-picker';

export const useQuestionnaireHomeController = (props: QuestionnaireHomeProps) => {
  const { onChange, questionnaire, showErrorMessage, getBaseFolders, initialQuestionnaire, getFhq4QuestionnaireField } = props;

  const [foldersForGenericLink, setFoldersForGenericLink] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [familyPagesSkipLogicConditions, setFamilyPagesSkipLogicConditions] = React.useState<SkipLogicCondition[]>([]);

  React.useEffect(() => {
    setFamilyPagesSkipLogicConditions(
      (questionnaire.homeTab.familyPagesSkipLogic ?? []).map((item): SkipLogicCondition => ({ ...item, id: generateId() } as SkipLogicCondition))
    );
  }, [questionnaire.questionnaireId]);

  React.useEffect(() => {
    getBaseFolders()
      .then((x) => {
        setFoldersForGenericLink(x);
      })
      .catch((err) => {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to get folders'))
      });
  }, []);

  const handleQuestionnaireNameChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      questionnaireName: val.trim(),
    });
  }, [questionnaire, onChange]);

  const getBuiltInPages = React.useCallback(async (
    quest: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto, 
    { currentSetFirstNameFieldInPages }: { currentSetFirstNameFieldInPages?: QuestionnaireFieldGetDto }
  )  => {
    let affectedFamilyFields: Array<QuestionnaireFieldGetDto> | undefined = undefined;
    let familyTableFields: Array<QuestionnaireFieldGetDto> | undefined = undefined;

    if (quest.questionnaireType === QuestionnaireType.family) {
      if (quest.homeTab.firstNameFieldId && !isNil(quest.homeTab.firstNameFieldSystem)) {
        try {
          const firstNameField = await getFhq4QuestionnaireField({
            id: quest.homeTab.firstNameFieldId,
            system: quest.homeTab.firstNameFieldSystem,
            context: FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT
          });
  
          affectedFamilyFields = [firstNameField];
          familyTableFields = [firstNameField];
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto | undefined)?.entity ?? I18n.t('Failed to preload field'));
        }
      }
    }

    const config: Record<QuestionnaireType, Partial<Record<QuestionnairePageType, { title: string; fields?: Array<QuestionnaireFieldGetDto> }>>> = {
      [QuestionnaireType.individual]: {
        [QuestionnairePageType.finalNotes]: { title: 'Final Notes' }
      },
      [QuestionnaireType.family]: {
        [QuestionnairePageType.affected]: { title: 'Affected family members', fields: affectedFamilyFields },
        [QuestionnairePageType.familyBuilder]: { title: 'Build your family' },
        [QuestionnairePageType.twin]: { title: 'Twin', },
        [QuestionnairePageType.nieceNephew]: { title: 'Siblings',  },
        [QuestionnairePageType.halfSibling]: { title: 'Half-Siblings', },
        [QuestionnairePageType.cousin]: { title: 'Aunts/Uncles', },
        [QuestionnairePageType.familyMembers]: { title: 'Describe your family', fields: familyTableFields },
        [QuestionnairePageType.finalNotes]: { title: 'Final Notes', },
      },
    };

    const currentBuiltInPages = config[quest.questionnaireType];

    const existingBuiltInPagesByType = initialQuestionnaire.designerTab.pages
      .filter((x) => (x as QuestionnairePageListDto).pageType !== QuestionnairePageType.normal)
      .reduce<Partial<Record<QuestionnairePageType, QuestionnairePageListDto | QuestionnairePageCreateLocalDto>>>((acc, page) => {
        acc[(page as QuestionnairePageListDto | QuestionnairePageCreateLocalDto).pageType] = page as QuestionnairePageListDto | QuestionnairePageCreateLocalDto;

        return acc;
      }, {});

    const getPages = (allowedPageTypes: Array<QuestionnairePageType>) => {
      const builtInPagesToDelete = Object.values(existingBuiltInPagesByType)
        .filter((x) => !allowedPageTypes.includes((x as QuestionnairePageListDto).pageType))
        .map((page) => {
          return {
            ...page,
            deleted: true,
          };
        });

      const builtInPagesToAdd = allowedPageTypes.map((pageType, i) => {
        const existingPage = existingBuiltInPagesByType[pageType] as QuestionnairePageUpdateDto | QuestionnairePageListDto | undefined;
        const defaultPageData = currentBuiltInPages[pageType];

        if (existingPage) {
          const defaultFields = defaultPageData?.fields ?? [];
          const exitingFields = existingPage.fields ?? [];
          const fields = exitingFields.length === 0
            ? defaultFields
            : [...defaultFields, ...exitingFields].filter((x) => {
              if (currentSetFirstNameFieldInPages) {
                return getUniqueFieldCombination({ id: x.progenyId, system: x.systemField }) !== 
                       getUniqueFieldCombination({ id: currentSetFirstNameFieldInPages.progenyId, system: currentSetFirstNameFieldInPages.systemField });
              }

              return true;
            });

          return {
            ...existingPage,
            title: existingPage.title || defaultPageData?.title || '',
            fields,
            order: i + 1,
            modified: true,
            enabled: true,
          };
        }

        return {
          isNotCreated: true,
          id: generateId(),
          order: i + 1,
          pageType,
          modified: true,
          enabled: true,
          skipLogicEnabled: false,
          fields: [],
          title: '',
          ...defaultPageData
        };
      });

      if ((initialQuestionnaire as QuestionnaireCreateLocalDto).isNotCreated) {
        return builtInPagesToAdd;
      }

      return [...builtInPagesToDelete, ...builtInPagesToAdd];
    };

    const result = getPages(Object.keys(currentBuiltInPages) as Array<QuestionnairePageType>);

    return result;
  }, [questionnaire, initialQuestionnaire]);

  const syncBuiltInPages = React.useCallback(async (
    quest: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto,
    { currentSetFirstNameFieldInPages }: { currentSetFirstNameFieldInPages?: { id: number; system: boolean } }
  ) => {
    let field: QuestionnaireFieldGetDto | undefined
    
    if (currentSetFirstNameFieldInPages) {
      field = await getFhq4QuestionnaireField({
        id: currentSetFirstNameFieldInPages.id,
        system: currentSetFirstNameFieldInPages.system,
        context: FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT,
      })
    }
    const normalPages = quest.designerTab.pages.filter((x) => (x as QuestionnairePageListDto).pageType === QuestionnairePageType.normal);
    const builtInPages = await getBuiltInPages(quest, { currentSetFirstNameFieldInPages: field });
    const nextPages = [...normalPages, ...builtInPages];

    onChange({
      ...quest,
      designerTab: {
        ...quest.designerTab,
        pages: nextPages.map((x, i) => ({ ...x, order: i + 1 })) as QuestionnaireDesignerTabUpdateLocalDto['pages'],
      },
    });
  }, [onChange, getBuiltInPages]);

  const handleQuestionnaireTypeChange = React.useCallback(async (val: QuestionnaireType) => {
    onChange({
      ...questionnaire,
      questionnaireType: val,
    });

    syncBuiltInPages(
      {
      ...questionnaire,
      questionnaireType: val,
      },
      {
        currentSetFirstNameFieldInPages: questionnaire.homeTab.firstNameFieldId 
          ? {
            id: questionnaire.homeTab.firstNameFieldId,
            system: questionnaire.homeTab.firstNameFieldSystem!,
          }
          : undefined
      }
    );
  }, [questionnaire, onChange, syncBuiltInPages]);

  const handleExitUrlChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        exitURL: val,
      },
    });
  }, [questionnaire, onChange]);

  const handleLanguageChange = React.useCallback((val: QuestionnaireLanguage) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        localeCode: val,
      },
    });
  }, [questionnaire, onChange]);

  const handleQuestionnaireStatusChange = React.useCallback(() => {
    onChange({
      ...questionnaire,
      enabled: !questionnaire.enabled
    });
  }, [questionnaire, onChange]);

  const handleExternalUrlChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        externalURL: val,
      },
    });
  }, [questionnaire, onChange]);

  const handleLogoUrlChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        loginLogoURL: val,
      },
    });
  }, [questionnaire, onChange]);

  const handleGenericLinkDefaultFolderChange = React.useCallback((val: FolderPrimaryListDto['id']) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        genlinkTargetFolderId: val,
      },
    });
  }, [questionnaire, onChange]);
  
  const handleGenericLinkChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        genericLink: val,
      },
    });
  }, [questionnaire, onChange]);

  const handlePatientFirstNameChange = React.useCallback(async (value: FieldPickerOnChangeValue | undefined) => {
    const nextBaseQuestionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto = {
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        firstNameFieldId: value ? value.field.id as number : undefined,
        firstNameFieldName: value ? value.field.name : undefined,
        firstNameFieldSystem: value ? value.field.system : undefined,
      },
    };

    onChange(nextBaseQuestionnaire);
    syncBuiltInPages(
      nextBaseQuestionnaire,
      {
        currentSetFirstNameFieldInPages: nextBaseQuestionnaire.homeTab.firstNameFieldId 
        ? {
          id: nextBaseQuestionnaire.homeTab.firstNameFieldId,
          system: nextBaseQuestionnaire.homeTab.firstNameFieldSystem!,
        }
        : undefined
      }
    );
  }, [questionnaire, onChange, syncBuiltInPages]);

  const handlePatientLastNameChange = React.useCallback((value: FieldPickerOnChangeValue | undefined) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        lastNameFieldId: value ? value.field.id as number : undefined,
        lastNameFieldName: value ? value.field.name : undefined,
        lastNameFieldSystem: value ? value.field.system : undefined,
      },
    });
  }, [questionnaire, onChange]);

  const handlePatientEmailChange = React.useCallback((value: FieldPickerOnChangeValue | undefined) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        patientEmailFieldId: value ? value.field.id as number : undefined,
        patientEmailFieldName: value ? value.field.name : undefined,
        patientEmailFieldSystem: value ? value.field.system : undefined,
      },
    });
  }, [questionnaire, onChange]);

  const handleInviteDatasheetChange = React.useCallback((value: FieldPickerOnChangeValue | undefined) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        inviteDSFormat: value ? value.field.id as number : undefined,
        inviteDSFormatName: value ? value.field.name : undefined,
      },
    });
  }, [questionnaire, onChange]);

  const handleUseFamilyPagesSkipLogicChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        useFamilyPagesSkipLogic: JSON.parse(val),
      },
    });
  }, [questionnaire, onChange]);

  const handleSkipLogicConditionAdd = React.useCallback(({ conditions }: { conditions: QuestionnaireSkipLogicConditionCreateDto[] }) => {
    const familyPagesSkipLogic: Array<SkipLogicCondition> = [
      ...familyPagesSkipLogicConditions,
      ...conditions.map((condition) => ({ ...condition, id: generateId(), operator: SkipLogicConditionOperator.equals } as SkipLogicCondition))
    ];

    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        familyPagesSkipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto),
      },
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [questionnaire, onChange, familyPagesSkipLogicConditions]);

  const handleSkipLogicChange = React.useCallback((familyPagesSkipLogic: SkipLogicCondition[]) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        familyPagesSkipLogic: familyPagesSkipLogic.map(({ id, ...condition }) => condition as QuestionnaireSkipLogicConditionListDto)
      },
    });
    setFamilyPagesSkipLogicConditions(familyPagesSkipLogic);
  }, [questionnaire, onChange]);

  const handleShowPrintAnswersChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        showPrintAnswers: val,
      },
    });
  }, [questionnaire, onChange]);

  const handleShowPedigreeChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        showPedigree: val,
      },
    });
  }, [questionnaire, onChange, questionnaire]);

  const handleSetPedigreeSymbol = React.useCallback((params?: FormatPickerOnChangeValue) => {
    if(!params?.format) {
      onChange({...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        pedSymbolFormat: undefined,
        pedSymbolFormatName: undefined,
      }})
      return;
    }

    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        pedSymbolFormat: params.format.id as number,
        pedSymbolFormatName: params.format.name as string,
      },
    });
  }, [onChange, questionnaire]);

  const handleSetPedigreeSubtext = React.useCallback((params?: FormatPickerOnChangeValue) => {
    if(!params?.format) {
      onChange({...questionnaire,
        homeTab: {
          ...questionnaire.homeTab,
          pedSubtextFormat: undefined,
          pedSubtextFormatName: undefined,
        }})
      return;
    }

    onChange({
      ...questionnaire,
      homeTab: {
        ...questionnaire.homeTab,
        pedSubtextFormat: params.format.id as number,
        pedSubtextFormatName: params.format.name as string,
      },
    });
  }, [onChange, questionnaire]);
  
  return {
    foldersForGenericLink,
    familyPagesSkipLogicConditions,
    handleLanguageChange,
    handleQuestionnaireTypeChange,
    handleShowPrintAnswersChange,
    handleShowPedigreeChange,
    handleInviteDatasheetChange,
    handlePatientEmailChange,
    handlePatientLastNameChange,
    handlePatientFirstNameChange,
    handleGenericLinkChange,
    handleGenericLinkDefaultFolderChange,
    handleLogoUrlChange,
    handleExternalUrlChange,
    handleExitUrlChange,
    handleQuestionnaireNameChange,
    handleUseFamilyPagesSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleSkipLogicChange,
    handleSetPedigreeSymbol,
    handleSetPedigreeSubtext,
    handleQuestionnaireStatusChange
  };
};